<script setup>
import {computed, ref, watch} from "vue";
import {useStore} from "vuex";
import ActionTabs from "@/components/actions/partials/ActionTabs.vue";
import ProgressBox from "@/components/actions/partials/ProgressBox.vue";
import ActionMenu from "@/components/actions/buttons/ActionMenu.vue";
import SettingsButton from "@/components/actions/buttons/SettingsButton.vue";
import PagedActions from "@/components/actions/tabs/PagedActions.vue";
import CompletedActions from "@/components/actions/tabs/CompletedActions.vue";
import ActionsTimeModal from "@/components/actions/partials/ActionsTimeModal.vue";
import {listenForWebsiteEvent, events} from "@/composables/sockets/sockets";
import {useQueryClient} from "@tanstack/vue-query";
import useWebsite from "@/composables/useWebsite";
import ActiveActions from "@/components/actions/tabs/ActiveActions.vue";
import {crawlInProgress, crawlStatusQuery} from "@/composables/crawler/useCrawler";
import LoadingBlock from "@/components/global-themed/LoadingBlock.vue";
import CrawlerOverlay from "@/components/actions/partials/settings/CrawlerOverlay.vue";
import {infoToast, successToast, warningToast} from "@/composables/useToasts";
import CategoryActions from "@/components/actions/tabs/CategoryActions.vue";

const store = useStore();
const activeTab = computed(() => store.getters['actions/activeTab']);
const {website, websiteId, isLoading: isWebsiteLoading } = useWebsite();

const parentContainerElement = ref(null);

const queryClient = useQueryClient();

const refreshActionQueries = () => {
  queryClient.invalidateQueries({ queryKey: ['actions'] });
  queryClient.invalidateQueries({ queryKey: ['action-categories'] });
  queryClient.invalidateQueries({ queryKey: ['action-progress'] });
}

watch(website, (newVal) => {
  if (!newVal) {
    return;
  }

  listenForWebsiteEvent(website.value.id, events.ACTION_COMPLETED, () => {
    if (crawlInProgress.value) {
      return;
    }
    successToast(
      'We’ve checked the update and can confirm it’s been applied correctly. Fantastic job – you’re making excellent progress!',
      'Great work! Your fix is live',
    )
    refreshActionQueries();
  });

  listenForWebsiteEvent(website.value.id, events.ACTION_CREATED, () => {
    if (crawlInProgress.value) {
      return;
    }
    infoToast(
      'We\'ve found some new actions and have added them to your to-do list.',
      'Checking your fix',
    )
    refreshActionQueries();
  });

  listenForWebsiteEvent(website.value.id, events.ACTION_MARKED_INCOMPLETE, () => {
    if (crawlInProgress.value) {
      return;
    }
    warningToast(
      'We’ve checked, and it looks like the issue hasn’t been resolved. Don’t worry – have another go, and if you’re stuck, our support team is here to help.',
      'Fix not quite there yet'
    )
    refreshActionQueries();
  });
});
const {isLoading} = crawlStatusQuery(websiteId);
</script>

<template>
  <div
    ref="parentContainerElement"
    class="tw-m-auto tw-max-w-default-container"
  >
    <div class="tw-flex tw-flex-col tw-gap-y-8">
      <div class="tw-flex tw-flex-center tw-justify-between">
        <h1 class="tw-font-bold tw-text-4.5xl">
          Actions
        </h1>
        <div class="tw-flex tw-items-stretch tw-gap-4 tw-h-13">
          <SettingsButton />
          <ActionMenu v-if="!crawlInProgress" />
        </div>
      </div>

      <div
        v-if="isLoading || isWebsiteLoading"
        class="tw-bg-white tw-p-8 tw-rounded-lg"
      >
        <LoadingBlock />
      </div>

      <CrawlerOverlay v-else-if="crawlInProgress" />

      <template v-else>
        <ProgressBox
          :parent-container-ref="parentContainerElement"
        />

        <div class="tw-bg-white tw-p-8 tw-rounded-lg tw-flex tw-flex-col tw-gap-y-10">
          <ActionTabs />
          <div>
            <ActiveActions v-if="activeTab === 'active'" />
            <PagedActions v-if="activeTab === 'page'" />
            <CategoryActions v-if="activeTab === 'category'" />
            <CompletedActions v-if="activeTab === 'completed'" />
          </div>
        </div>
      </template>
    </div>
    <ActionsTimeModal />
  </div>
</template>
